import { useCallback, useEffect, useState } from "react";
import { Check, Inbox, RefreshCw, RotateCcw, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const EMAIL_LABEL = { sent: "Inbox email sent", simulated: "Inbox email simulated", failed: "Inbox email failed", pending: "Inbox email pending" };

export const ContactEnquiries = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("new");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setError("");
    try { const response = await api.get("/admin/contact-enquiries", { params: filter === "all" ? {} : { status: filter } }); setData(response.data); }
    catch { setError("Enquiries could not be loaded. Please retry."); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    setBusy(id);
    try { await api.patch(`/admin/contact-enquiries/${id}`, { status }); await load(); }
    catch { setError("Could not update this enquiry. Please retry."); }
    finally { setBusy(null); }
  };
  return <section data-testid="admin-contact-enquiries" className="space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h2 className="ff-cabinet text-lg font-bold" data-testid="enquiries-title">Website enquiries</h2>
        <p className="text-sm text-slate-600" data-testid="enquiries-new-count">{data ? `${data.new_count} new` : "Loading enquiries…"}</p></div>
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" role="group" aria-label="Filter enquiries">
          {[["new", "New"], ["handled", "Handled"], ["all", "All"]].map(([key, label]) => <button key={key} type="button" onClick={() => setFilter(key)} aria-pressed={filter === key} className={`rounded px-3 py-1 text-xs font-semibold ${filter === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`enquiries-filter-${key}`}>{label}</button>)}
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="enquiries-refresh"><RefreshCw className="h-4 w-4" />Refresh</Button>
      </div>
    </div>
    {error && <p role="alert" className="text-sm text-red-700" data-testid="enquiries-error">{error}</p>}
    {data?.items.length === 0 && <p className="border-y py-8 text-sm text-slate-600" data-testid="enquiries-empty"><Inbox className="mr-2 inline h-4 w-4" />No {filter === "all" ? "" : filter} enquiries.</p>}
    <div className="divide-y">{data?.items.map((item) => <article key={item.id} className="flex flex-wrap items-start gap-3 py-5" data-testid={`enquiry-${item.id}`}>
      <Mail className="mt-1 h-5 w-5 shrink-0 text-slate-500" />
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold" data-testid={`enquiry-name-${item.id}`}>{item.name}{item.company ? <span className="font-normal text-slate-500"> · {item.company}</span> : null}</h3>
        <a href={`mailto:${item.email}`} className="break-all text-sm text-brand-ink hover:underline" data-testid={`enquiry-email-${item.id}`}>{item.email}</a>
        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700" data-testid={`enquiry-message-${item.id}`}>{item.message}</p>
        <p className="mt-2 text-xs text-slate-500" data-testid={`enquiry-meta-${item.id}`}>{new Date(item.created_at).toLocaleString()} · {EMAIL_LABEL[item.email_status] || item.email_status}{item.handled_by ? ` · handled by ${item.handled_by}` : ""}</p>
      </div>
      {item.status === "new"
        ? <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setStatus(item.id, "handled")} className="gap-1" data-testid={`enquiry-handle-${item.id}`}><Check className="h-4 w-4" />Mark handled</Button>
        : <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setStatus(item.id, "new")} className="gap-1 text-slate-600" data-testid={`enquiry-reopen-${item.id}`}><RotateCcw className="h-4 w-4" />Reopen</Button>}
    </article>)}</div>
  </section>;
};
